import React, { useState, useEffect } from "react";
import { bool, string, func, number, arrayOf } from "prop-types";

import CollapsableContainer from "@components/shared/CollapsableContainer";
import ErrorMessage from "@components/shared/ErrorMessage";
import CircularLoader from "@components/shared/Icons/CircularLoader";
import FinancingTitle from "@components/Financing/FinancingTitle";

import { useIsSCFJourneySelected } from "@hooks/useIsSCFJourneySelected";

import CashFinancing from "./CashFinancing";
import FinanceFinancing from "./FinanceFinancing";
import TotalFinanceFinancing from "./TotalFinanceFinancing";

import { JOURNEY_TYPE } from "@shared/constants";
import { getIsCashJourneySelected } from "@shared/v2/lib/getIsCashJourneySelected";
import { getIsLeasingJourneySelected } from "@shared/v2/lib/getIsLeasingJourneySelected";
import { useGetJourneySpecificConfig } from "@hooks/useGetJourneySpecificConfig";

import {
  useLOAPriceBreakdownSelector,
  useLLDPriceBreakdownSelector,
  useVACPriceBreakdownSelector,
  useSCFPriceBreakdownSelector,
  useVehicleQuantitySelector,
  useIsB2BLeasysSelected,
} from "@redux/reducers/deal";

import {
  useLLDPriceBreakdownRequestStatusSelector,
  useLOAPriceBreakdownRequestStatusSelector,
  useVACPriceBreakdownRequestStatusSelector,
  useSCFPriceBreakdownRequestStatusSelector,
} from "@redux/reducers/requests";

import { useIsKeyAccount } from "@redux/reducers/auth";

import {
  dealItemType,
  financingLOAData,
  financingCashData,
  financingLLDData,
  financingVACData,
  financingSCFData,
} from "@types";

export const FINANCING_COLLAPSABLE_CONTAINER =
  "financing-collapsable-container";

const getFinanceProductRelatedProps = data => {
  const {
    journey,
    lldData,
    lldPriceBreakdown,
    loaData,
    loaPriceBreakdown,
    scfData,
    scfPriceBreakdown,
    vacData,
    vacPriceBreakdown,
    isModifyVisible,
    isLLDRequestError,
    isLLDRequestPending,
    isLOARequestError,
    isLOARequestPending,
    isVACRequestError,
    isVACRequestPending,
    isSCFRequestError,
    isSCFRequestPending,
  } = data;

  switch (journey) {
    case JOURNEY_TYPE.CASH:
      return {};
    case JOURNEY_TYPE.LLD:
      return {
        financialProductLabel: lldData.financialProductLabel,
        displayBlocks: lldData.displayBlocks,
        legalMention: lldData.legalMention,
        shouldDisplayError: isLLDRequestError,
        customizeButtonText:
          lldPriceBreakdown?.vehicleSimulation?.result.teaser.callToAction.text,
        isModifiable: isModifyVisible && !isLLDRequestPending,
      };

    case JOURNEY_TYPE.LOA:
      return {
        financialProductLabel: loaData.financialProductLabel,
        displayBlocks: loaData.displayBlocks,
        legalMention: loaData.legalMention,
        shouldDisplayError: isLOARequestError,
        customizeButtonText:
          loaPriceBreakdown?.vehicleSimulation?.result.teaser.callToAction.text,
        isModifiable: isModifyVisible && !isLOARequestPending,
      };

    case JOURNEY_TYPE.VAC:
      return {
        financialProductLabel: vacData?.financialProductLabel,
        displayBlocks: vacData?.displayBlocks,
        legalMention: vacData?.legalMention,
        shouldDisplayError: isVACRequestError,
        customizeButtonText:
          vacPriceBreakdown?.vehicleSimulation?.result.teaser.callToAction.text,
        isModifiable: isModifyVisible && !isVACRequestPending,
      };

    case JOURNEY_TYPE.SCF:
      return {
        financialProductLabel: scfData?.financialProductLabel,
        displayBlocks: scfData?.displayBlocks,
        legalMention: scfData?.legalMention,
        shouldDisplayError: isSCFRequestError,
        customizeButtonText:
          scfPriceBreakdown?.vehicleSimulation?.result.teaser.callToAction
            .detailText,
        isModifiable: isModifyVisible && !isSCFRequestPending,
      };

    default:
      throw new Error(
        `getFinanceProductRelatedProps can't be called on journey '${journey}'`
      );
  }
};

const Financing = props => {
  const {
    deal,
    cashData,
    lldData,
    loaData,
    scfData,
    vacData,

    bankTransferPaymentCompleted = null,
    className,
    disabled,
    errorMessages,
    generateContainerId = false,
    index = 2,
    isB2BSelected,
    isGenericHeader,
    isLoading,
    isModifyVisible,
    isReadOnly = false,
    journey,
    onChange,
    opened,
    revertProgress,
    withDeliveryInfo,
  } = props;

  const {
    isPending: isLLDRequestPending,
    isError: isLLDRequestError,
  } = useLLDPriceBreakdownRequestStatusSelector();

  const {
    isPending: isLOARequestPending,
    isError: isLOARequestError,
  } = useLOAPriceBreakdownRequestStatusSelector();

  const {
    isPending: isVACRequestPending,
    isError: isVACRequestError,
  } = useVACPriceBreakdownRequestStatusSelector();

  const {
    isPending: isSCFRequestPending,
    isError: isSCFRequestError,
  } = useSCFPriceBreakdownRequestStatusSelector();

  const loaPriceBreakdown = useLOAPriceBreakdownSelector();
  const lldPriceBreakdown = useLLDPriceBreakdownSelector();
  const vacPriceBreakdown = useVACPriceBreakdownSelector();
  const scfPriceBreakdown = useSCFPriceBreakdownSelector();
  const quantity = useVehicleQuantitySelector();
  const { multipleVehicles: quantitySettings } = useGetJourneySpecificConfig();

  const isKeyAccount = useIsKeyAccount();
  const isB2BLeasysSelected = useIsB2BLeasysSelected();

  const isSCFJourneySelected = useIsSCFJourneySelected();

  const isQuantityVisible = quantitySettings?.enabled;

  const [isCollapsableContainerOpen, setIsCollapsableContainerOpen] = useState(
    opened ?? false
  );

  const isCashJourneySelected = getIsCashJourneySelected(journey);

  const financeProps = getFinanceProductRelatedProps({
    journey,
    lldData,
    lldPriceBreakdown,
    loaData,
    loaPriceBreakdown,
    scfData,
    scfPriceBreakdown,
    vacData,
    vacPriceBreakdown,
    isModifyVisible,
    isLLDRequestError,
    isLLDRequestPending,
    isLOARequestError,
    isLOARequestPending,
    isVACRequestError,
    isVACRequestPending,
    isSCFRequestError,
    isSCFRequestPending,
  });

  useEffect(() => {
    setIsCollapsableContainerOpen(opened);
  }, [opened]);

  if (isKeyAccount && isB2BLeasysSelected) {
    return null;
  }

  const innerOnChange = currentIsOpen => {
    setIsCollapsableContainerOpen(!currentIsOpen);

    onChange?.(currentIsOpen);
  };

  const isFinanceProductDataMissing =
    getIsLeasingJourneySelected(journey) &&
    !financeProps?.financialProductLabel;

  const isLeasingJourneyError =
    isLLDRequestError ||
    isVACRequestError ||
    isLOARequestError ||
    isSCFRequestError;

  return (
    <CollapsableContainer
      className={{ wrap: className }}
      title={
        <FinancingTitle
          isGenericHeader={isGenericHeader}
          journey={journey}
          vehicleQuantity={quantity}
        />
      }
      titleIndex={index}
      isOpen={isCollapsableContainerOpen}
      isDisabled={disabled}
      onChange={innerOnChange}
      id={
        FINANCING_COLLAPSABLE_CONTAINER +
        (generateContainerId ? `_${deal.gitId}` : "")
      }
    >
      <>
        {isLoading ||
        (isFinanceProductDataMissing && !isLeasingJourneyError) ? (
          <div className="flex justify-center items-center">
            <CircularLoader className="size-40 p-4 text-primaryOrange" />
          </div>
        ) : (
          <>
            {isCashJourneySelected ? (
              <CashFinancing
                bankTransferPaymentCompleted={bankTransferPaymentCompleted}
                cashData={cashData}
                deal={deal}
                isB2BSelected={isB2BSelected}
                isModifiable={isModifyVisible}
                isQuantityVisible={isQuantityVisible}
                isReadOnly={isReadOnly}
                onModifyButtonClick={revertProgress}
                vehicleQuantity={quantity}
                withDeliveryInfo={withDeliveryInfo}
              />
            ) : (
              <>
                <FinanceFinancing
                  isWidgetEditable={!isSCFJourneySelected && !isReadOnly}
                  onModifyButtonClick={revertProgress}
                  withDeliveryInfo={withDeliveryInfo}
                  journey={journey}
                  isQuantityVisible={isQuantityVisible}
                  vehicleQuantity={quantity}
                  isReadOnly={isReadOnly}
                  deal={deal}
                  {...financeProps}
                />
                {isQuantityVisible && (
                  <TotalFinanceFinancing
                    isQuantityVisible={isQuantityVisible}
                    journey={journey}
                    vehicleQuantity={quantity}
                    {...financeProps}
                  />
                )}
              </>
            )}
            {errorMessages?.length > 0 &&
              errorMessages.map((errorMessage, index) => (
                <ErrorMessage key={index}>{errorMessage}</ErrorMessage>
              ))}
          </>
        )}
      </>
    </CollapsableContainer>
  );
};

Financing.propTypes = {
  deal: dealItemType,
  cashData: financingCashData,
  lldData: financingLLDData,
  loaData: financingLOAData,
  scfData: financingSCFData,
  vacData: financingVACData,

  bankTransferPaymentCompleted: bool,
  className: string,
  disabled: bool,
  errorMessages: arrayOf(string),
  generateContainerId: bool,
  index: number,
  isB2BSelected: bool.isRequired,
  isCurrentStep: bool,
  isFinanceLoading: bool,
  isGenericHeader: bool,
  isLoading: bool,
  isModifyVisible: bool,
  isOpen: bool,
  isReadOnly: bool,
  journey: string.isRequired,
  onChange: func,
  onCollapseContainerChange: func,
  opened: bool,
  revertProgress: func,
  withDeliveryInfo: bool,
};

export default Financing;
